import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { parseTableParams, formatDate } from '../../utils/Utils';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import DataTable from '../../components/ui/DataTable';

const Container = styled.div``;

export default function MyCertificatesPage() {
  const { t, i18n } = useTranslation();

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'trainingTitle', label: t('training.title'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'certificateNumber',
      label: t('training.certificateNumber'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      ...dateFilterColumn('issuedAt', t('training.certificateIssuedAt'), i18n.language),
      options: {
        ...dateFilterColumn('issuedAt', t('training.certificateIssuedAt'), i18n.language).options,
        customBodyRender: (v) => v ? formatDate(v, i18n.language) : '—',
      },
    },
    {
      ...dateFilterColumn('expiresAt', t('training.certificateExpires'), i18n.language),
      options: {
        ...dateFilterColumn('expiresAt', t('training.certificateExpires'), i18n.language).options,
        customBodyRender: (v) => v ? formatDate(v, i18n.language) : '—',
      },
    },
  ], [t, i18n.language]);

  const fetchData = useCallback((params) => {
    setFetching(true);
    api.get('/v1/training-certificates/my', { params })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, []);

  useMemo(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('training.myCertificates')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange])}
    </Container>
  );
}
